// =====================================================================
// Flattening a Twilio preflight result into one loggable row.
//
// Production shows ~19 round trips between the browser accepting a call and any
// audio arriving, so the wait scales with the SDR's latency to their Twilio edge
// (~1s at 32ms RTT, ~7s at 360ms). What no live-call telemetry can say is WHICH
// phase those round trips are spent in, or whether media is being relayed.
//
// `Device.runPreflight` answers both, and — the reason it is worth a button —
// it places a test call to Twilio's echo service rather than to a lead. An SDR
// can run it once without fielding a real call, which matters when the people
// best placed to diagnose this are the ones the problem drove away.
//
// Pure: the caller owns the SDK and the logging, so the shape is testable
// without a browser, a token, or a call.
// =====================================================================

// The fields we read off `PreflightTest.Report`. Declared structurally rather than
// imported so this module stays free of the SDK — and so a shape change in the SDK
// surfaces here as a type error at the call site instead of a silent null.
export interface PreflightReportLike {
  callSid?: string | undefined;
  edge?: string;
  selectedEdge?: string;
  isTurnRequired?: boolean;
  callQuality?: string;
  networkTiming?: {
    signaling?: { duration?: number };
    ice?: { duration?: number };
    dtls?: { duration?: number };
    peerConnection?: { duration?: number };
  };
  testTiming?: { duration?: number };
  selectedIceCandidatePairStats?: {
    localCandidate?: { candidateType?: string; protocol?: string };
    remoteCandidate?: { candidateType?: string; protocol?: string };
  };
  stats?: {
    rtt?: { average?: number; max?: number };
    jitter?: { average?: number; max?: number };
    mos?: { average?: number; min?: number };
  };
  warnings?: unknown[];
}

export interface PreflightLog {
  // Index signature so this satisfies the logger's context type directly. Every field is
  // still declared below, so a typo in a key is still a type error at construction.
  [key: string]: string | number | boolean | null;
  event: 'cockpit_preflight';
  call_sid: string | null;
  // Which edge the test actually reached. `roaming` picks by latency and there is no
  // African edge, so this says whether a distant SDR is correctly on their nearest one.
  edge: string | null;
  selected_edge: string | null;
  // The TURN question, answered outright by the SDK: true when either selected
  // candidate is a relay, which doubles the media path and adds allocation round trips.
  turn_required: boolean | null;
  // The ~19 round trips, split by phase. This is the whole point of the exercise —
  // `first_media_ms` from a live call gives the total and nothing else.
  signaling_ms: number | null;
  ice_ms: number | null;
  dtls_ms: number | null;
  peer_connection_ms: number | null;
  total_ms: number | null;
  // host | srflx | prflx | relay. `relay` on either end is the TURN case above.
  local_candidate: string | null;
  remote_candidate: string | null;
  local_protocol: string | null;
  remote_protocol: string | null;
  rtt_avg: number | null;
  rtt_max: number | null;
  jitter_avg: number | null;
  mos_min: number | null;
  call_quality: string | null;
  warning_count: number;
}

// `undefined` becomes null so a missing measurement is explicit in the log rather than
// an absent key, and rounded so the values bucket cleanly in a query.
const ms = (v: number | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null;

const str = (v: string | undefined): string | null => (typeof v === 'string' && v ? v : null);

const num = (v: number | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Number(v.toFixed(3)) : null;

export function buildPreflightLog(report: PreflightReportLike): PreflightLog {
  const timing = report.networkTiming ?? {};
  const pair = report.selectedIceCandidatePairStats ?? {};
  const stats = report.stats ?? {};

  return {
    event: 'cockpit_preflight',
    call_sid: str(report.callSid),
    edge: str(report.edge),
    selected_edge: str(report.selectedEdge),
    // Explicitly nullable: the SDK leaves it undefined when no candidate pair was
    // selected, which is a different fact from "no relay was needed".
    turn_required: typeof report.isTurnRequired === 'boolean' ? report.isTurnRequired : null,
    signaling_ms: ms(timing.signaling?.duration),
    ice_ms: ms(timing.ice?.duration),
    dtls_ms: ms(timing.dtls?.duration),
    peer_connection_ms: ms(timing.peerConnection?.duration),
    total_ms: ms(report.testTiming?.duration),
    local_candidate: str(pair.localCandidate?.candidateType),
    remote_candidate: str(pair.remoteCandidate?.candidateType),
    local_protocol: str(pair.localCandidate?.protocol),
    remote_protocol: str(pair.remoteCandidate?.protocol),
    rtt_avg: num(stats.rtt?.average),
    rtt_max: num(stats.rtt?.max),
    jitter_avg: num(stats.jitter?.average),
    mos_min: num(stats.mos?.min),
    call_quality: str(report.callQuality),
    warning_count: Array.isArray(report.warnings) ? report.warnings.length : 0,
  };
}

// One line for the SDR, who ran this because someone asked them to and deserves to see
// that it did something. Deliberately not a verdict on their connection: the numbers are
// for us, and telling an SDR their network is "bad" invites them to go fix a thing that
// may well be physics.
export function preflightSummary(log: PreflightLog): string {
  if (log.total_ms == null) return 'Test finished — results sent to the team.';
  const seconds = (log.total_ms / 1000).toFixed(1);
  return `Test finished in ${seconds}s — results sent to the team.`;
}
